import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import {
  FeatureSubscriptionType,
  PortalMembership,
  PortalPermissions,
  usePortalMembership_UpdateAssignedPortalOwnerMutation,
  User_GetPortalAccountUsersFieldsFragment,
  useUser_GetPortalAccountUsersQuery,
  useUser_ReInvitePortalUserMutation,
  useUser_SetPortalUserAccountDisabledMutation,
} from 'queries';
import { MoreVertical } from 'ts-react-feather-icons';

import Spinner from 'common/spinner';
import { canCollabWithTeamMembers } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useCurrentUser from 'utils/useCurrentUser';
import useHasPortalPermission from 'utils/useHasPortalPermission';
import useIsPortalOwner from 'utils/useIsPortalOwner';

import InviteUserModal from './invite-user-modal';

type Props = {
  featureSubscriptionType: FeatureSubscriptionType;
  portalOwner: User_GetPortalAccountUsersFieldsFragment;
};

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  menuHeader: {
    paddingLeft: 5,
    cursor: 'default',
  },
  description: {
    marginRight: '10px',
  },
  listAction: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default function ManagePortalUsers({ featureSubscriptionType, portalOwner }: Props) {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const currentUser = useCurrentUser();
  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);
  const isPortalOwner = useIsPortalOwner();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);

  const { data: usersData, loading: usersLoading } = useUser_GetPortalAccountUsersQuery({
    variables: {
      input: {
        portalId,
        includeAccountDisabledUsers: true,
        includeClientUsers: true,
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting portal account users: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [updatePortalOwner] = usePortalMembership_UpdateAssignedPortalOwnerMutation({
    onCompleted: () => {
      showToast({
        message: 'The account owner has been updated.',
        severity: NotificationSeverity.SUCCESS,
      });
    },
    onError: (e) => {
      showAlert({
        message: 'There was an issue assigning the new account owner: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [setPortalUserAccountDisabled] = useUser_SetPortalUserAccountDisabledMutation({
    onCompleted: () =>
      showToast({
        message: 'User updated successfully',
        severity: NotificationSeverity.SUCCESS,
      }),
    onError: (e) =>
      showAlert({
        message: 'Error updating user: ' + e.message,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const [reInvitePortalUser] = useUser_ReInvitePortalUserMutation({
    onCompleted: () =>
      showToast({
        message: 'User re-invited successfully',
        severity: NotificationSeverity.SUCCESS,
      }),
    onError: (e) =>
      showAlert({
        message: 'Error re-inviting user: ' + e.message,
        severity: NotificationSeverity.ERROR,
      }),
  });

  if (!currentUser) {
    return <Spinner />;
  }
  if (!usersData || usersLoading) {
    return <Spinner />;
  }

  const handleMenuClick = () => {
    setAnchorEl(null);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleActionClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleEnableOrDisableUser = async (
    userId: string,
    accountDisabled: boolean,
    permission: PortalPermissions | 'OWNER',
  ) => {
    if (currentUser!.id === userId) {
      showAlert({
        message: 'Unable to disabled your own account.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    const actionVerb = accountDisabled ? 'disable' : 'enable';

    // if the current user is the account owner they can disable all accounts
    if (currentUser!.id === portalOwner.id) {
      if (!window.confirm(`Are you sure you want to ${actionVerb} this user?`)) {
        return;
      }

      await setPortalUserAccountDisabled({
        variables: {
          input: { portalId, userId, accountDisabled },
        },
      });
      return;
    }

    // prevent admin's from disabling admins and owner
    if (permission === 'OWNER' || permission === PortalPermissions.Admin) {
      showAlert({
        message: 'Owner and Admin accounts cannot be disabled.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to ${actionVerb} this user?`)) {
      return;
    }

    await setPortalUserAccountDisabled({
      variables: {
        input: { portalId, userId, accountDisabled },
      },
    });
  };

  const handleMakeUserPortalOwner = async (userId: string) => {
    if (!window.confirm('Are you sure you want to make this user the portal owner?')) {
      return;
    }

    await updatePortalOwner({
      variables: {
        portalId,
        targetUserId: userId,
      },
    });
  };

  const handleReInvitePortalUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to re-invite this user?')) {
      return;
    }

    reInvitePortalUser({
      variables: {
        input: {
          portalId,
          userId,
        },
      },
    });
  };

  const userColumns: GridColDef[] = [
    {
      disableColumnMenu: true,
      field: 'firstName',
      minWidth: 200,
      headerName: 'Name',
      renderCell: (params: GridRenderCellParams) => {
        const fullName = params.row.firstName + ' ' + params.row.lastName;
        return (
          <>
            <Tooltip title={fullName}>
              <Typography className={classes.description}>{fullName}</Typography>
            </Tooltip>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) =>
        params.row.firstName + ' ' + params.row.lastName,
    },
    {
      disableColumnMenu: true,
      field: 'email',
      minWidth: 200,
      headerName: 'Email',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <Tooltip title={params.row.email}>
              <Typography className={classes.description}>{params.row.email}</Typography>
            </Tooltip>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) => params.row.email,
    },
    {
      disableColumnMenu: true,
      field: 'phone',
      minWidth: 200,
      headerName: 'Phone Number',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <Typography className={classes.description}>{params.row.phone}</Typography>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) => params.row.phone,
    },
    {
      disableColumnMenu: true,
      field: 'emailVerified',
      minWidth: 150,
      headerName: 'Status',
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.portalMemberships) {
          return 'Invalid Portal Membership'; // TODO - needed to add this check because for some reason the relation wasn't available on initial page load
        }
        const portalMembership = params.row.portalMemberships.find(
          (portalMem: PortalMembership) => {
            return portalMem.portal!.id === portalId;
          },
        );
        let statusText =
          params.row.emailVerified && portalMembership?.isDefault ? 'Member' : 'Invited';
        if (params.row.accountDisabled) {
          statusText = 'Disabled';
        }
        return (
          <>
            <Typography className={classes.description}>{statusText}</Typography>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        if (!params.row.portalMemberships) {
          return 'Invalid Portal Membership'; // TODO - needed to add this check because for some reason the relation wasn't available on initial page load
        }
        const portalMembership = params.row.portalMemberships.find(
          (portalMem: PortalMembership) => {
            return portalMem.portal!.id === portalId;
          },
        );
        let statusText =
          params.row.emailVerified && portalMembership?.isDefault ? 'Member' : 'Invited';
        if (params.row.accountDisabled) {
          statusText = 'Disabled';
        }
        return statusText;
      },
    },
    {
      disableColumnMenu: true,
      field: 'portalMemberships',
      minWidth: 150,
      headerName: 'Permission',
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row.portalMemberships) {
          return 'Invalid Portal Membership'; // TODO - needed to add this check because for some reason the relation wasn't available on initial page load
        }
        const portalMembership = params.row.portalMemberships.find(
          (portalMem: PortalMembership) => {
            return portalMem.portal!.id === portalId;
          },
        );
        const permissionText =
          portalOwner.id === params.row.id ? 'OWNER' : portalMembership?.permission;
        const rowUserIsAdminOrOwner = permissionText === 'ADMIN' || permissionText === 'OWNER';
        const accountDisabled = params.row.accountDisabled;
        const isRowUserAdmin = portalMembership?.permission === PortalPermissions.Admin;
        return (
          <>
            <Typography className={classes.description}>{permissionText}</Typography>
            <List style={{ position: 'absolute', right: '0px' }}>
              <ListItem>
                <ListItemSecondaryAction className={classes.listAction}>
                  <IconButton
                    id={params.row.id}
                    edge="end"
                    onClick={(e) => {
                      handleActionClick(e);
                    }}
                    aria-controls={params.row.id}
                    size="large"
                  >
                    <MoreVertical />
                  </IconButton>
                  <Menu
                    open={anchorEl?.id === params.row.id}
                    anchorEl={anchorEl}
                    keepMounted
                    onClose={() => handleMenuClick()}
                  >
                    <MenuItem className={classes.menuHeader}>User Actions</MenuItem>
                    {hasPortalPermissionAdmin &&
                      !params.row.emailVerified &&
                      !params.row.accountDisabled && (
                        <MenuItem
                          key="re-invite user"
                          onClick={() => {
                            handleMenuClick();
                            handleReInvitePortalUser(params.row.id);
                          }}
                        >
                          Re-invite
                        </MenuItem>
                      )}
                    {hasPortalPermissionAdmin && (
                      <MenuItem
                        key="enable/disable user"
                        onClick={() => {
                          handleMenuClick();
                          handleEnableOrDisableUser(
                            params.row.id,
                            !accountDisabled,
                            permissionText,
                          );
                        }}
                        disabled={!isPortalOwner && rowUserIsAdminOrOwner}
                      >
                        Change status: {accountDisabled ? 'Enabled' : 'Disabled'}
                      </MenuItem>
                    )}
                    {hasPortalPermissionAdmin &&
                      portalOwner.id === currentUser!.id &&
                      params.row.id !== portalOwner.id &&
                      (portalMembership?.permission === PortalPermissions.Admin ||
                        portalMembership?.permission === PortalPermissions.User) &&
                      params.row.emailVerified &&
                      !params.row.accountDisabled && (
                        <Tooltip
                          title={!isRowUserAdmin ? 'User must be upgraded to Admin' : ''}
                          disableHoverListener={isRowUserAdmin}
                        >
                          <span>
                            <MenuItem
                              key="enable/disable user"
                              onClick={() => {
                                handleMenuClick();
                                handleMakeUserPortalOwner(params.row.id);
                              }}
                              disabled={!isRowUserAdmin}
                            >
                              Make Portal Owner
                            </MenuItem>
                          </span>
                        </Tooltip>
                      )}
                  </Menu>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        if (!params.row.portalMemberships) {
          return 'Invalid Portal Membership'; // TODO - needed to add this check because for some reason the relation wasn't available on initial page load
        }
        const portalMembership = params.row.portalMemberships.find(
          (portalMem: PortalMembership) => {
            return portalMem.portal!.id === portalId;
          },
        );
        const permissionText =
          portalOwner.id === params.row.id ? 'OWNER' : portalMembership?.permission;
        const accountDisabled = params.row.accountDisabled;

        return accountDisabled ? 'Account Disabled' : permissionText;
      },
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userRows: any[] = [];
  const users = usersData ? usersData.data : [];
  for (const user of users) {
    userRows.push(user);
  }

  return (
    <>
      {hasPortalPermissionAdmin && (
        <>
          <InviteUserModal showModal={showInviteUserModal} setShowModal={setShowInviteUserModal} />
          <div style={{ flex: 3 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ marginTop: '3px', marginBottom: '6px', padding: '2px 10px' }}
              onClick={() => {
                if (!canCollabWithTeamMembers(featureSubscriptionType)) {
                  showToast({
                    message: 'Please upgrade your account to collaborate with team members.',
                    severity: NotificationSeverity.WARNING,
                  });
                  return;
                }

                setShowInviteUserModal(true);
              }}
            >
              + Invite a New User
            </Button>
          </div>
        </>
      )}
      <Grid container spacing={2}>
        <Grid item md={12} xs={12}>
          <div style={{ width: '100%' }}>
            <DataGrid
              className={classes.root}
              columns={userColumns}
              rows={userRows}
              disableColumnReorder
              disableRowSelectionOnClick
              autoHeight={true}
              rowHeight={65}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 100 } },
              }}
              components={{
                Toolbar: () => (
                  <Box className={classes.menuHeader}>
                    <Typography variant="h5">Users</Typography>
                    <hr />
                  </Box>
                ),
              }}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
}
